// PRE RMA — Saisie unique multi-programmes : tableaux DÉRIVÉS de la même consultation.
// Une consultation saisie une seule fois (Tableau 4 / RCE) alimente automatiquement la vue PCIME
// (enfants de moins de 5 ans) — sans ressaisie. Aucune table supplémentaire : tout est recalculé
// à partir de ConsultationRow avec les MÊMES fonctions de tranche que le RMA (source unique).
//
// ⚠️ À CONFIRMER avec la fiche officielle T8_Paludisme_PCIME : la liste PCIME_DIAGNOSTICS ci-dessous
// est le choix de départ (pathologies de l'enfant < 5 ans déjà présentes dans les diagnostics RCE).
// La modifier ne change que cette vue ; le RMA existant n'est pas touché.

import { trancheMorbidite, tranchePalu, trancheNutrition } from './tranches.ts';
import { RULES } from './constants.ts';
import type { ConsultationRow } from './rma.ts';

export const PCIME_DIAGNOSTICS = [
  'PNEUMONIE', 'PNEUMONIE_SEVERE', 'IRA', 'DIARRHEE_SANS_DH2O', 'DYSENTERIE',
  'PALUDISME_SIMPLE', 'PALUDISME_GRAVE', 'SIGNE_IBG', 'OTITE', 'ANEMIE', 'MAS', 'MAM',
] as const;

/** Tranches de morbidité strictement inférieures à 5 ans (dérivées de RULES, jamais recopiées). */
export const TRANCHES_PCIME = RULES.tranches.morbidite.filter((b) => b.jusquaJours < RULES.age.seuils.cinqAns).map((b) => b.label);

export interface LignePcime { code: string; libelle: string; parTranche: Record<string, number>; total: number }

export function calculerPcime(rows: ConsultationRow[]): { lignes: LignePcime[]; totalEnfants: number } {
  const cols = TRANCHES_PCIME.flatMap((t) => [`${t} M`, `${t} F`]);
  const lignes = PCIME_DIAGNOSTICS.map((code) => {
    const parTranche: Record<string, number> = Object.fromEntries(cols.map((c) => [c, 0]));
    for (const r of rows) {
      if (!r.numeroRceBrut.trim() || r.ageJours === null || !r.genre || r.ageJours >= RULES.age.seuils.cinqAns) continue;
      if (!r.diagnostics.includes(code)) continue;
      parTranche[trancheMorbidite(r.ageJours, r.genre)] += 1;
    }
    const d = RULES.diagnostics.find((x) => x.code === code);
    return { code, libelle: d?.libelle ?? code, parTranche, total: Object.values(parTranche).reduce((a, b) => a + b, 0) };
  });
  const totalEnfants = rows.filter((r) => r.numeroRceBrut.trim() && r.ageJours !== null && r.ageJours < RULES.age.seuils.cinqAns).length;
  return { lignes, totalEnfants };
}

export interface ImpactTableau { tableau: string; detail: string }

/**
 * « Que va alimenter cette saisie ? » — affiché sous le formulaire de Saisie unique pour montrer
 * à l'agent que la donnée est déjà comptée dans les autres tableaux (aucune ressaisie).
 */
export function apercuImpactSaisie(r: Pick<ConsultationRow, 'ageJours' | 'genre' | 'diagnostics' | 'casFievre' | 'tdrResultat' | 'numeroRceBrut'>): ImpactTableau[] {
  const out: ImpactTableau[] = [];
  if (r.ageJours === null || !r.genre || !r.numeroRceBrut.trim()) return out;
  const diagLib = (c: string) => RULES.diagnostics.find((d) => d.code === c)?.libelle ?? c;
  const tr = trancheMorbidite(r.ageJours, r.genre);
  for (const d of r.diagnostics) out.push({ tableau: 'Tableau 4 – Diagnostics', detail: `${diagLib(d)} · ${tr} +1` });
  if (r.ageJours < RULES.age.seuils.cinqAns) {
    for (const d of r.diagnostics) if ((PCIME_DIAGNOSTICS as readonly string[]).includes(d)) out.push({ tableau: 'Tableau 8 – PCIME (< 5 ans)', detail: `${diagLib(d)} · ${tr} +1` });
  }
  const palu = r.diagnostics.some((d) => (RULES.diagnosticsPaludisme as readonly string[]).includes(d));
  if (palu || r.casFievre === 'Oui' || r.tdrResultat) out.push({ tableau: 'Tableau 8 – Paludisme', detail: `Tranche ${tranchePalu(r.ageJours)}${r.tdrResultat ? ` · TDR ${r.tdrResultat}` : ''} +1` });
  const nutr = trancheNutrition(r.ageJours);
  if (nutr && r.diagnostics.some((d) => d === 'MAS' || d === 'MAM')) out.push({ tableau: 'Tableau 11 – Nutrition', detail: `Tranche ${nutr} +1` });
  return out;
}
